/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListTriggerEventsRequest,
  ListTriggerEventsSecurity,
} from "../models/operations/listtriggerevents.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildTriggerEventsQuery,
  prefetchTriggerEvents,
  queryKeyTriggerEvents,
  TriggerEventsQueryData,
} from "./triggerEvents.core.js";
export {
  buildTriggerEventsQuery,
  prefetchTriggerEvents,
  queryKeyTriggerEvents,
  type TriggerEventsQueryData,
};

export type TriggerEventsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listTriggerEvents triggers
 *
 * @remarks
 * List recent dispatch events for a trigger instance.
 */
export function useTriggerEvents(
  request: ListTriggerEventsRequest,
  security?: ListTriggerEventsSecurity | undefined,
  options?: QueryHookOptions<TriggerEventsQueryData, TriggerEventsQueryError>,
): UseQueryResult<TriggerEventsQueryData, TriggerEventsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildTriggerEventsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listTriggerEvents triggers
 *
 * @remarks
 * List recent dispatch events for a trigger instance.
 */
export function useTriggerEventsSuspense(
  request: ListTriggerEventsRequest,
  security?: ListTriggerEventsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    TriggerEventsQueryData,
    TriggerEventsQueryError
  >,
): UseSuspenseQueryResult<TriggerEventsQueryData, TriggerEventsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildTriggerEventsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setTriggerEventsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: TriggerEventsQueryData,
): TriggerEventsQueryData | undefined {
  const key = queryKeyTriggerEvents(...queryKeyBase);

  return client.setQueryData<TriggerEventsQueryData>(key, data);
}

export function invalidateTriggerEvents(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "listEvents", ...queryKeyBase],
  });
}

export function invalidateAllTriggerEvents(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "listEvents"],
  });
}
